import { cn } from "cn";
import { Check } from "lucide-react";
import type * as React from "react";
import { useId } from "react";

// The v3.2 checkbox (style.css .check): a 20px rounded square on the sunken
// surface that fills with ink when ticked, inside a 44px row on phones so
// the whole label is the tap target. A native input underneath, so forms,
// keyboards and screen readers work as usual.
export function Checkbox({
  checked,
  onCheckedChange,
  label,
  description,
  disabled = false,
  className,
  testId,
  id,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: React.ReactNode;
  /** A second line under the label. */
  description?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  testId?: string;
  id?: string;
}): React.JSX.Element {
  const generated = useId();
  const inputId = id ?? generated;
  return (
    <label
      className={cn(
        "group flex min-h-11 cursor-pointer items-start gap-3 py-2.5 font-v3 text-[15px] leading-[22px] text-v3-text lg:min-h-9 lg:py-1.5 lg:text-[14px] lg:leading-5",
        disabled && "cursor-not-allowed opacity-50",
        className,
      )}
      data-slot="checkbox"
      htmlFor={inputId}
    >
      <span className="relative mt-px flex size-5 shrink-0 items-center justify-center">
        <input
          checked={checked}
          className="peer absolute inset-0 m-0 cursor-[inherit] appearance-none rounded-[6px] bg-v3-surface-2 shadow-v3-hairline-strong transition-colors checked:bg-v3-ink checked:shadow-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-brand"
          data-testid={testId}
          disabled={disabled}
          id={inputId}
          type="checkbox"
          onChange={(event) => onCheckedChange(event.target.checked)}
        />
        <Check
          aria-hidden
          className="pointer-events-none relative size-3.5 text-v3-on-ink opacity-0 peer-checked:opacity-100"
          strokeWidth={3}
        />
      </span>
      <span className="min-w-0">
        <span className="block">{label}</span>
        {description && (
          <span className="block text-[13px] leading-[18px] text-v3-text-3">
            {description}
          </span>
        )}
      </span>
    </label>
  );
}
